import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { XIcon } from "lucide-react";
import { useTimecode } from "../../../hooks/useTimecode";
import { useLoggerOffset } from "../hooks/useLoggerOffset";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { ParticipantsPanel } from "./ParticipantsPanel";
import { ParticipantBadge } from "../../common/components/ParticipantBadge";
import { useTags } from "../../common/contexts/TagsContext";
import { LiveTranscriptionsPanel } from "./LiveTranscriptionsPanel";
import { RecentLogsPanel } from "./RecentLogsPanel";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";

import { Story, LogEntry, RundownItem } from "../types";
import { StoryCreationInput } from "./StoryCreationInput";
import { CustomDialog } from "../../common/components/CustomDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { StarRating } from "@/components/ui/star-rating";
import { ButtonGroup } from "@/components/ui/button-group";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useProjects } from "../../common/contexts/ProjectContext";
import { isColorLight } from "../../../utils/participantColors";
import { compareLogs } from "../../../utils/logOrder";
import { createTempLogId } from "../utils/logIdentity";

interface ConsoleColumnProps {
  activeStory: Story;
  activeRundown?: RundownItem;
  rundownTitle?: string;
  rundownItems?: RundownItem[]; // Added prop
  onAddLog: (log: LogEntry) => boolean | void;
  onUpdateLog: (log: LogEntry) => void;
  onDeleteLog?: (logId: string) => void;
  onCreateStory: (title: string, rundownId?: string) => void;
  onCompleteStory: (id: string) => void;
  onAddSideLog: (log: LogEntry) => void;
  externalEditingLog?: LogEntry;
  externalEditingStory?: Story | null;
  onExternalUpdate?: (log: LogEntry) => void;
  onExternalCancel?: () => void;
  onExternalStoryCancel?: () => void;
  onExternalDelete?: () => void;
  participants: any[];
  loggerName?: string;
  onUpdateLoggerName?: (name: string) => void;
  onUpdateStory?: (story: Story) => void;
  onEditStory?: (story: Story) => void;
  clearSignal?: number;
  availableCameras?: any[];
  onReorderLogs?: (orderedIds: string[]) => void;
}

const _VerticalSizeControl: React.FC<{
  value: number;
  onChange: (val: number) => void;
  min: number;
  max: number;
  step?: number;
}> = ({ value, onChange, min, max, step = 10 }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      background: "rgba(255,255,255,0.02)",
      border: "1px solid var(--color-panel-border)",
      borderRadius: "10px",
      width: "22px",
      padding: "6px 0",
      alignSelf: "center",
      flexShrink: 0,
      gap: "6px",
    }}
  >
    <Button
      variant="ghost"
      size="icon-xs"
      className="size-4 text-muted-foreground"
      onClick={() => onChange(Math.min(max, value + step))}
    >
      +
    </Button>
    <Button
      variant="ghost"
      size="icon-xs"
      className="size-4 text-muted-foreground"
      onClick={() => onChange(Math.max(min, value - step))}
    >
      -
    </Button>
  </div>
);

// Stepper control: shadcn ButtonGroup with minus/plus buttons
const SizeSlider: React.FC<{
  value: number;
  onChange: (val: number) => void;
  min: number;
  max: number;
  step?: number;
  "data-testid"?: string;
}> = ({ value, onChange, min, max, step = 10, "data-testid": testId }) => {
  const decrement = () => onChange(Math.max(min, value - step));
  const increment = () => onChange(Math.min(max, value + step));

  return (
    <ButtonGroup data-testid={testId} className="ml-2.5">
      <Button
        type="button"
        variant="outline"
        size="icon-xs"
        onClick={decrement}
        disabled={value <= min}
        aria-label="Decrease size"
      >
        <i className="bx bx-minus"></i>
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon-xs"
        onClick={increment}
        disabled={value >= max}
        aria-label="Increase size"
      >
        <i className="bx bx-plus"></i>
      </Button>
    </ButtonGroup>
  );
};

export const ConsoleColumn: React.FC<ConsoleColumnProps> = ({
  activeStory,
  activeRundown,
  rundownTitle,
  rundownItems,
  onAddLog,
  onUpdateLog,
  onDeleteLog,
  onReorderLogs,
  onCreateStory,
  onCompleteStory,
  onAddSideLog,
  externalEditingLog,
  externalEditingStory,
  onExternalUpdate,
  onExternalCancel,
  onExternalStoryCancel,
  onExternalDelete,
  participants,
  loggerName: _loggerName,
  onUpdateLoggerName: _onUpdateLoggerName,
  onUpdateStory,
  onEditStory,
  clearSignal: _clearSignal,
  availableCameras,
}) => {
  const { t } = useTranslation();
  const {
    getTagLabel,
    getTagColor,
    getCameraName,
    categories,
    tags,
    cameras: contextCameras,
    directors,
  } = useTags();
  const cameras = availableCameras || contextCameras;
  const isMobile = useIsMobile();

  const getParticipantName = (id: string) => {
    const p = participants.find((p) => p.id.toLowerCase() === id.toLowerCase());
    if (!p) return id;
    return p.number ? `${p.number} ${p.alias || p.name}` : p.alias || p.name;
  };
  // Correctly closed previously.

  const offsetSec = useLoggerOffset();

  const MATRIX_SPLIT_KEY = "console.matrixSplitLayout";
  const [matrixSplitLayout, setMatrixSplitLayout] = React.useState<
    Record<string, number> | undefined
  >(() => {
    if (typeof window === "undefined") return undefined;
    try {
      const raw = window.localStorage.getItem(MATRIX_SPLIT_KEY);
      return raw ? JSON.parse(raw) : undefined;
    } catch {
      return undefined;
    }
  });

  const { currentDisplayTC, frozenTC, frozenAt, freeze, unfreeze } = useTimecode({
    offsetMs: offsetSec * 1000,
  });

  const [inputValue, setInputValue] = React.useState("");
  // Socket echoes replace activeStory while the operator types. Keep the title
  // draft local until blur so those echoes cannot drop characters or move caret.
  const [titleDraft, setTitleDraft] = React.useState(activeStory.title);
  const titleFocusedRef = React.useRef(false);
  React.useEffect(() => {
    if (!titleFocusedRef.current) setTitleDraft(activeStory.title);
  }, [activeStory.id, activeStory.title]);
  const [editingLogId, setEditingLogId] = React.useState<string | null>(null);
  const [editingLogTC, setEditingLogTC] = React.useState<string | null>(null);
  const [editingOriginalLog, setEditingOriginalLog] = React.useState<LogEntry | null>(null);
  const [viewingParticipant, setViewingParticipant] = React.useState<any | null>(null);

  const [isCreatingStory, setIsCreatingStory] = React.useState(false);
  const [showConfirmClose, setShowConfirmClose] = React.useState(false);
  const [showTranscriptions, setShowTranscriptions] = React.useState(true);
  const [isOtfMode, setIsOtfMode] = useLocalStorage<boolean>("logger_otfMode", false);

  const { activeProject } = useProjects();
  const [isAiActive, setIsAiActive] = React.useState(false);

  const handleStartAi = async (channel: number) => {
    try {
      const inputId = `session-${activeProject?.id || "default"}-ch${channel}`;
      const res = await fetch("/api/transcription/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          audioSource: `rtmp://relay.local/live/stream${channel}`,
          projectId: activeProject?.id,
          inputId: inputId,
          language: "es",
        }),
      });
      if (res.ok) setIsAiActive(true);
    } catch (e) {
      console.error(e);
    }
  };

  const handleStopAi = async (channel: number) => {
    try {
      const inputId = `session-${activeProject?.id || "default"}-ch${channel}`;
      const res = await fetch("/api/transcription/stop", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ inputId }),
      });
      if (res.ok) setIsAiActive(false);
    } catch (e) {
      console.error(e);
    }
  };

  // Temporal Speaker Mapping definition
  type SpeakerAssignment = {
    speakerId: string;
    name: string;
    assignedAt: number;
  };
  const [speakerAssignments, setSpeakerAssignments] = React.useState<SpeakerAssignment[]>([]);
  const [draggedTranscriptionTimecodes, setDraggedTranscriptionTimecodes] = React.useState<
    string[] | null
  >(null);
  const [showConfirmComplete, setShowConfirmComplete] = React.useState(false);
  const [dialog, setDialog] = React.useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "alert" | "prompt" | "confirm";
    onConfirm: (val?: string) => void;
    defaultValue?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "alert",
    onConfirm: () => {},
  });

  const lastLogs = useMemo<LogEntry[]>(() => {
    if (!activeStory) return [];
    return [...(activeStory.logs || [])].sort(compareLogs).map((log) => ({
      ...log,
      storyTags: activeStory.tags,
      storyParticipants: activeStory.participants,
      storyRating: activeStory.rating,
      // El stream asignado al log gana sobre la cámara por defecto de la
      // historia. Así una transcripción del STREAM 2 no termina en STREAM 1
      // cuando se consulta después desde Biblioteca o Cortes.
      streams: log.streams?.length ? log.streams : activeStory.cameras,
      storyDirectorId: activeStory.directorId,
      rundownName: log.rundownName || rundownTitle,
      eventName:
        log.eventName ||
        activeRundown?.topic ||
        (activeRundown as any)?.title ||
        (activeRundown as any)?.name,
      storyName: log.storyName || activeStory.title,
    }));
  }, [
    activeStory?.logs,
    activeStory?.tags,
    activeStory?.participants,
    activeStory?.rating,
    activeStory?.cameras,
    activeStory?.directorId,
    activeStory?.title,
    rundownTitle,
    activeRundown?.topic,
    (activeRundown as any)?.title,
    (activeRundown as any)?.name,
  ]);

  const [viewingMilestoneId, setViewingMilestoneId] = React.useState<string | null>(() => {
    // Persist state to avoid losing context on refresh
    return localStorage.getItem("viewingMilestoneId") || null;
  });

  // Save to localStorage whenever it changes
  useEffect(() => {
    if (viewingMilestoneId) {
      localStorage.setItem("viewingMilestoneId", viewingMilestoneId);
    }
  }, [viewingMilestoneId]);

  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  // Blocks duplicate transcript double-clicks until the upstream story update
  // has returned. Checking lastLogs alone is stale during that short window.
  const recentQuickLogsRef = React.useRef<Map<string, number>>(new Map());
  // Tracks caret position from the last time the user interacted with the
  // textarea, so drops from external panels insert at the user's cursor
  // instead of always appending to the end.
  const lastCaretRef = React.useRef<{ start: number; end: number } | null>(null);
  const captureCaret = () => {
    const ta = inputRef.current;
    if (!ta) return;
    lastCaretRef.current = { start: ta.selectionStart, end: ta.selectionEnd };
  };
  const isHandoverPending =
    activeRundown && viewingMilestoneId && activeRundown.id !== viewingMilestoneId;

  // DEBUG: Soft Handover (Removed)

  useEffect(() => {
    if (!activeRundown) return;
    // Solo actualizamos automáticamente si es la primera vez (null)
    if (viewingMilestoneId === null) {
      setViewingMilestoneId(activeRundown.id);
    }
  }, [activeRundown?.id, viewingMilestoneId]);

  const handleJoinLive = () => {
    if (!activeRundown) return;
    // 1. Create New Story for the new Milestone
    const newTitle = `STORY OF ${activeRundown.title}`;
    onCreateStory(newTitle, activeRundown.id);

    // 2. Switch View (This removes the alert)
    setViewingMilestoneId(activeRundown.id);
  };

  const handleCreateNewStory = React.useCallback(
    (title: string) => {
      // Use viewingMilestoneId so a pending handover doesn't redirect new stories to the unjoined event.
      const targetRundownId = viewingMilestoneId || activeRundown?.id;
      if (onCreateStory) onCreateStory(title, targetRundownId);
      setIsCreatingStory(false);
    },
    [onCreateStory, viewingMilestoneId, activeRundown?.id],
  );

  const handleCancelCreation = React.useCallback(() => {
    setIsCreatingStory(false);
  }, []);

  useEffect(() => {
    if (isCreatingStory) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.target instanceof HTMLSelectElement) return;
      // Ignore keystrokes inside dropdown menus (stream/director selectors, etc.)
      const el = e.target as HTMLElement;
      if (el.closest?.('[role="menu"], [role="listbox"], [data-radix-popper-content-wrapper]'))
        return;
      const role = el.getAttribute?.("role");
      if (role && ["menuitemradio", "menuitem", "option", "combobox"].includes(role)) return;
      const ignoredKeys = ["Shift", "Control", "Alt", "Meta", "CapsLock", "Tab", "Escape"];
      if (ignoredKeys.includes(e.key)) return;
      if (!frozenTC) freeze();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [frozenTC, freeze, isCreatingStory]);

  // Persisted display settings (localStorage)
  const [partSizePhoto, setPartSizePhoto] = useLocalStorage("logger_partSizePhoto", 110);
  const [partSizeText, setPartSizeText] = useLocalStorage("logger_partSizeText", 140);
  const [cameraSize, setCameraSize] = useLocalStorage("logger_cameraSize", 100);
  const [participantMode, setParticipantMode] = useLocalStorage<"photo" | "number" | "code">(
    "logger_participantMode",
    "photo",
  );
  const [showTagText, setShowTagText] = useLocalStorage("logger_showTagText", true);
  const [hiddenParticipantIds, setHiddenParticipantIds] = useLocalStorage<string[]>(
    "logger_hiddenParticipantIds",
    [],
  );
  const [hiddenCameraIds, setHiddenCameraIds] = useLocalStorage<string[]>(
    "logger_hiddenCameraIds",
    [],
  );
  const [tagSizes, setTagSizes] = useLocalStorage("logger_tagSizes", { text: 100, icon: 40 });

  // Non-persisted transient state
  const [selectedCameras, setSelectedCameras] = useLocalStorage<string[]>(
    "logger_selectedCameras",
    [],
  );
  const [selectedLogCameras, setSelectedLogCameras] = useLocalStorage<string[]>(
    "logger_selectedLogCameras",
    [],
  );

  React.useEffect(() => {
    // Entry-level stream defaults to empty — the story-level stream
    // already covers all entries. Only set explicitly for overrides.
    setSelectedLogCameras([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStory?.id]);

  const [selectedDirector, setSelectedDirector] = useLocalStorage<string | undefined>(
    "logger_selectedDirector",
    undefined,
  );
  // Persisted defaults apply to every new log, side log and transcript created
  // from this console. Per-entry selections remain additive.
  const [selectedLocation, setSelectedLocation] = useLocalStorage<string | undefined>(
    "logger_selectedLocation",
    undefined,
  );
  const [globalSelectedParticipants, setGlobalSelectedParticipants] = useLocalStorage<string[]>(
    "logger_globalSelectedParticipants",
    [],
  );
  const [selectedParticipants, setSelectedParticipants] = React.useState<string[]>([]);
  const locationTags = React.useMemo(() => {
    const locationCategory = categories.find((category) =>
      /espacios?|ubicaci|locaci|locations?/i.test(category.name || ""),
    );
    if (!locationCategory) return [] as typeof tags;
    return tags
      .filter((tag) => tag.categoryId === locationCategory.id)
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [categories, tags]);
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [isCameraFilterOpen, setIsCameraFilterOpen] = React.useState(false);
  const tagSize = showTagText ? tagSizes.text : tagSizes.icon;
  const setTagSize = (val: number | ((prev: number) => number)) => {
    setTagSizes((prev) => {
      const currentVal = showTagText ? prev.text : prev.icon;
      const newVal = typeof val === "function" ? val(currentVal) : val;
      return { ...prev, [showTagText ? "text" : "icon"]: newVal };
    });
  };

  // Dynamic size based on mode
  const participantSize = participantMode === "photo" ? partSizePhoto : partSizeText;
  const setParticipantSize = participantMode === "photo" ? setPartSizePhoto : setPartSizeText;

  const toggleSelection = (
    id: string,
    currentList: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    if (!frozenTC) freeze();
    setList((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const [rating, setRating] = React.useState<number>(0);
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  const [editingStoryData, setEditingStoryData] = React.useState<Story | null>(null);

  const toggleTag = (tagId: string) => {
    if (!frozenTC) freeze();
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId],
    );
  };

  React.useEffect(() => {
    if (externalEditingLog) {
      setEditingOriginalLog(externalEditingLog);
      setEditingLogId(externalEditingLog.id);
      setEditingLogTC(externalEditingLog.tc);
      setInputValue(externalEditingLog.text);
      setRating(externalEditingLog.rating || 0);
      setSelectedTags(externalEditingLog.tags || []);
      setSelectedLogCameras(externalEditingLog.cameras || []);
      setSelectedParticipants(externalEditingLog.participants || []);
      setSelectedDirector(externalEditingLog.directorId);
    }
  }, [externalEditingLog]);

  React.useEffect(() => {
    if (externalEditingStory) {
      setEditingStoryData(externalEditingStory);
      setInputValue(externalEditingStory.title || "");
      setRating(externalEditingStory.rating || 0);
      setSelectedTags(externalEditingStory.tags || []);
      setSelectedCameras(externalEditingStory.cameras || []);
      setSelectedParticipants(externalEditingStory.participants || []);
      setSelectedDirector(externalEditingStory.directorId);
      setEditingLogId(null);
      setEditingLogTC(null);
      if (frozenTC) unfreeze();
    }
  }, [externalEditingStory]);

  const extractMetadata = (txt: string) => {
    const matches = txt.match(/\[(.*?)\]/g);
    const extractedParticipants: string[] = [];
    const extractedCameras: string[] = [];
    const extractedTags: string[] = [];

    if (matches) {
      matches.forEach((m) => {
        const content = m.slice(1, -1).trim().toUpperCase();

        // Participants
        const p = participants.find(
          (part) =>
            (part.name && part.name.toUpperCase() === content) ||
            (part.alias && part.alias.toUpperCase() === content) ||
            (part.code && part.code.toUpperCase() === content) ||
            (part.number && `${part.number}` === content),
        );
        if (p) extractedParticipants.push(p.id);

        // Cameras
        const c = cameras.find((cam) => cam.name.toUpperCase() === content);
        if (c) extractedCameras.push(c.id);

        // Tags
        const t = tags.find((tag) => tag.label.toUpperCase() === content);
        if (t) extractedTags.push(t.id);
      });
    }
    return { extractedParticipants, extractedCameras, extractedTags };
  };

  // Every element that feeds the story history (log, side log, transcription)
  // must resolve to a stream. An entry inherits the story stream
  // (activeStory.cameras) when it has no explicit STREAM chip, so we only block
  // when neither the entry nor the story resolves to one. Returns false and
  // warns the operator when no stream is selected so callers can abort.
  const ensureStream = (entryCameras: string[]): boolean => {
    if (entryCameras.length === 0 && !activeStory?.cameras?.length) {
      toast.warning(t("logger.console.streamRequired"));
      return false;
    }
    return true;
  };

  const submitLog = () => {
    if (!frozenTC && inputValue.trim() === "") return;

    const { extractedParticipants, extractedCameras, extractedTags } = extractMetadata(inputValue);

    // Merge global defaults, per-entry selections and text mentions. The
    // name/alias check below still prevents a mention from duplicating a picker.
    const finalParticipants = Array.from(
      new Set([...globalSelectedParticipants, ...selectedParticipants]),
    );
    extractedParticipants.forEach((eId) => {
      if (!finalParticipants.includes(eId)) {
        const extractedP = participants.find((p) => p.id === eId);
        const nameConflict = finalParticipants.some((fId) => {
          const finalP = participants.find((p) => p.id === fId);
          return (
            finalP &&
            extractedP &&
            ((finalP.name && finalP.name.toUpperCase() === extractedP.name.toUpperCase()) ||
              (finalP.alias && finalP.alias.toUpperCase() === extractedP.alias.toUpperCase()) ||
              (finalP.code && finalP.code.toUpperCase() === extractedP.code.toUpperCase()))
          );
        });
        if (!nameConflict) finalParticipants.push(eId);
      }
    });

    const finalStoryCameras = Array.from(new Set([...selectedCameras, ...extractedCameras]));
    const effectiveLogCameras =
      selectedLogCameras.length > 0 ? selectedLogCameras : selectedCameras;
    const finalLogCameras = Array.from(new Set([...effectiveLogCameras, ...extractedCameras]));
    const finalTags = Array.from(
      new Set([...selectedTags, ...extractedTags, ...(selectedLocation ? [selectedLocation] : [])]),
    );

    let finalInput = inputValue;
    let forcedType: string | undefined = undefined;
    let forcedOriginType: LogEntry["originType"] | undefined;
    if (finalInput.includes("[OTF] ")) {
      forcedType = "otf";
      finalInput = finalInput.replace(/\[OTF\] /g, "");
    } else if (
      finalInput.match(/\[\d{2}:\d{2}:\d{2}:\d{2}\]/) ||
      finalInput.match(/^.*\[[^\]]+\]:/)
    ) {
      forcedType = "transcription";
    } else if (isOtfMode) {
      forcedType = "otf";
      forcedOriginType = "log";
    }

    if (editingStoryData) {
      const updatedStory: Story = {
        ...editingStoryData,
        title: finalInput,
        tags: finalTags,
        cameras: finalStoryCameras,
        participants: finalParticipants,
        rating: rating > 0 ? rating : undefined,
        directorId: selectedDirector,
      };
      if (onUpdateStory) {
        onUpdateStory(updatedStory);
      }
      cancelEdit();
    } else if (editingLogId) {
      const updatedLog: LogEntry = {
        ...(editingOriginalLog || ({} as any)),
        id: editingLogId,
        tc: editingLogTC || currentDisplayTC,
        text: finalInput,
        cameras: finalLogCameras,
        participants: finalParticipants,
        rating: rating > 0 ? rating : undefined,
        tags: finalTags.length > 0 ? finalTags : undefined,
        status: "synced",
        type: forcedType || (editingOriginalLog as any)?.type,
        directorId: selectedDirector,
      };

      if (externalEditingLog && externalEditingLog.id === editingLogId && onExternalUpdate) {
        onExternalUpdate(updatedLog);
      } else if (onUpdateLog) {
        onUpdateLog(updatedLog);
      }
      cancelEdit();
    } else {
      if (!ensureStream(finalLogCameras)) return;
      const submittedAt = Date.now();
      const newLog: LogEntry = {
        id: createTempLogId(forcedType || "event"),
        tc: frozenTC || currentDisplayTC,
        text: finalInput,
        cameras: finalLogCameras,
        participants: finalParticipants,
        rating: rating > 0 ? rating : undefined,
        tags: finalTags.length > 0 ? finalTags : undefined,
        status: "synced",
        type: forcedType || "event",
        originType: forcedOriginType,
        createdAt: submittedAt,
        startAt: (frozenAt ?? new Date(submittedAt)).toISOString(),
        directorId: selectedDirector,
      } as any;

      // Only clear the input when onAddLog reports success. Returning false
      // means the handler aborted (e.g. session expired) — clearing the input
      // in that case made it look like the log "vanished".
      let added: boolean | void = true;
      if (onAddLog) {
        added = onAddLog({ ...newLog, rundownId: viewingMilestoneId || undefined });
      }

      if (added === false) return;

      unfreeze();
      setInputValue("");
      // Keep selectedCameras (streams) — user wants it to persist between entries
      // Keep selectedDirector — user wants it to persist between entries
      setSelectedParticipants([]);
      setRating(0);
      setSelectedTags([]);

      // Removed auto-switch logic.
      // User must explicitly click "JOIN LIVE" to switch to the new event.
      // if (activeRundown && viewingMilestoneId !== activeRundown.id) {
      //    setViewingMilestoneId(activeRundown.id);
      // }
    }
  };

  const handleSideLog = () => {
    if (!onAddSideLog) return;
    const { extractedParticipants, extractedCameras, extractedTags } = extractMetadata(inputValue);
    const finalParticipants = Array.from(
      new Set([...globalSelectedParticipants, ...selectedParticipants, ...extractedParticipants]),
    );
    const effectiveSideCameras =
      selectedLogCameras.length > 0 ? selectedLogCameras : selectedCameras;
    const finalCameras = Array.from(new Set([...effectiveSideCameras, ...extractedCameras]));
    const finalTags = Array.from(
      new Set([...selectedTags, ...extractedTags, ...(selectedLocation ? [selectedLocation] : [])]),
    );

    // Side logs feed the same story history, so they need a stream too.
    if (!ensureStream(finalCameras)) return;

    const submittedAt = Date.now();
    const newLog: LogEntry = {
      id: createTempLogId(isOtfMode ? "otf" : "event"),
      tc: frozenTC || currentDisplayTC,
      text: inputValue,
      cameras: finalCameras,
      participants: finalParticipants,
      rating: rating > 0 ? rating : undefined,
      tags: finalTags.length > 0 ? finalTags : undefined,
      status: "synced",
      type: isOtfMode ? "otf" : undefined,
      originType: isOtfMode ? "log" : undefined,
      createdAt: submittedAt,
      startAt: (frozenAt ?? new Date(submittedAt)).toISOString(),
      directorId: selectedDirector,
    };
    onAddSideLog(newLog);
    unfreeze();
    setInputValue("");
    // Keep selectedCameras (streams) — user wants it to persist between entries
    // Keep selectedDirector — user wants it to persist between entries
    setSelectedParticipants([]);
    setRating(0);
    setSelectedTags([]);
  };

  const handleEditLog = (log: LogEntry) => {
    setEditingOriginalLog(log);
    setEditingLogId(log.id);
    setEditingLogTC(log.tc);
    setInputValue(log.text);
    setSelectedLogCameras(log.cameras || []);
    setSelectedDirector(log.directorId);
    setSelectedParticipants(log.participants || []);
    setRating(log.rating || 0);
    setSelectedTags(log.tags || []);
    if (frozenTC) unfreeze();
  };

  const cancelEdit = () => {
    setEditingOriginalLog(null);
    setEditingLogId(null);
    setEditingLogTC(null);
    setEditingStoryData(null);
    setInputValue("");
    setSelectedLogCameras([]);
    setSelectedParticipants([]);
    setRating(0);
    setSelectedTags([]);
    if (frozenTC) unfreeze();
    if (onExternalCancel) onExternalCancel();
    if (onExternalStoryCancel) onExternalStoryCancel();
  };

  return (
    <div
      data-testid="console-column"
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        background: "var(--color-panel-bg)",
        border: isHandoverPending ? "3px solid #ffcc00" : "1px solid var(--color-panel-border)",
        transition: "border 0.3s ease",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {isHandoverPending && (
        <div
          data-testid="console-handover-alert"
          style={{
            background: "#ffcc00",
            color: "#000",
            padding: "6px 20px",
            fontSize: "0.8rem",
            fontWeight: "900",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            animation: "pulse-warning 2s infinite",
            zIndex: 100,
          }}
        >
          <span>
            {t("logger.console.rundownUpdated")}
            {activeRundown?.title?.toUpperCase() || t("logger.console.newItem")}
            {t("logger.console.isLive")}
          </span>
          <Button
            data-testid="console-btn-join-live"
            variant="secondary"
            size="sm"
            className="bg-black text-white/80 hover:bg-black/80"
            onClick={handleJoinLive}
          >
            JOIN LIVE
          </Button>
          <span style={{ fontSize: "0.7rem", opacity: 0.7 }}>(FINISH YOUR NOTE TO SWITCH)</span>
        </div>
      )}

      <div
        data-testid="console-header"
        style={{
          padding: "12px 20px",
          borderBottom: "1px solid var(--color-panel-border)",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          background: "var(--color-bg-base)",
        }}
      >
        <div
          data-testid="console-header-row1"
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "16px",
            flexWrap: "nowrap",
            overflowX: "auto",
          }}
        >
          {/* RUNDOWN & EVENT BREADCRUMB */}
          <Breadcrumb className="shrink-0">
            <BreadcrumbList className="flex-nowrap gap-2 text-base">
              <BreadcrumbItem>
                <BreadcrumbLink
                  data-testid="console-rundown-box"
                  className="rounded-md bg-[var(--color-data-rundown)] px-3 py-1.5 text-sm font-black tracking-wide text-black shadow-[0_4px_12px_rgba(234,179,8,0.3)] hover:text-black hover:opacity-90 no-underline hover:no-underline flex items-center h-full"
                >
                  {rundownTitle?.toUpperCase() ||
                    activeRundown?.title?.toUpperCase() ||
                    t("logger.console.noRundown")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              {(() => {
                let currentItem = activeRundown;
                let idx = -1;

                if (viewingMilestoneId && rundownItems) {
                  idx = rundownItems.findIndex((i) => i.id === viewingMilestoneId);
                  if (idx !== -1) currentItem = rundownItems[idx];
                }

                if (!currentItem && activeRundown) {
                  currentItem = activeRundown;
                  idx = rundownItems ? rundownItems.findIndex((i) => i.id === activeRundown.id) : 0;
                }

                const _eventNumber = idx >= 0 ? `#${idx + 1}` : "#--";
                const eventTitle = currentItem?.title || t("logger.console.noEventSelected");

                return (
                  <BreadcrumbItem className="min-w-0 max-w-[260px]">
                    <BreadcrumbPage
                      data-testid="console-event-title"
                      className="truncate rounded-md bg-[var(--color-data-event)] px-3 py-1.5 text-sm font-black tracking-wide text-white shadow-[0_4px_12px_rgba(59,130,246,0.3)] flex items-center h-full"
                    >
                      {eventTitle.toUpperCase()}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                );
              })()}
            </BreadcrumbList>
          </Breadcrumb>

          {/* Story title and actions share this row with the rundown breadcrumb. */}
          <div
            data-testid="console-story-row"
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "16px",
              flexWrap: "nowrap",
              flexShrink: 0,
            }}
          >
            <div className="flex items-center gap-2 shrink-0">
              <i
                className="bx bx-book-open"
                style={{ fontSize: "1.25rem", color: "var(--color-data-story)" }}
              ></i>

              <Input
                data-testid="console-story-title-input"
                value={titleDraft}
                onFocus={() => {
                  titleFocusedRef.current = true;
                }}
                onChange={(e) => {
                  const title = e.target.value;
                  setTitleDraft(title);
                  onUpdateStory?.({ ...activeStory, title });
                }}
                onBlur={() => {
                  titleFocusedRef.current = false;
                }}
                placeholder={t("logger.console.storyTitlePlaceholder")}
                className="h-8 w-[220px] shrink-0 border-transparent bg-transparent text-sm font-bold text-[var(--color-data-story)] tracking-wide focus-visible:ring-0 focus-visible:border-b focus-visible:border-[var(--color-data-story)] focus-visible:rounded-none shadow-none"
              />
              {/* Botón Editar Activa */}
              {onEditStory && activeStory && activeStory.id !== "EMPTY" && (
                <Button
                  variant="outline"
                  size="icon-sm"
                  className="ml-1 shrink-0 text-[var(--color-data-story)] border-white/10 hover:bg-[var(--color-data-story)] hover:text-black"
                  onClick={() => onEditStory(activeStory)}
                  title={t("logger.console.editStoryMeta")}
                >
                  <i className="bx bx-pencil" style={{ fontSize: "1.2rem" }}></i>
                </Button>
              )}
              {/* Tags de la Historia Activa */}
              {!editingStoryData &&
              activeStory &&
              activeStory.id !== "EMPTY" &&
              (activeStory.cameras?.length ||
                activeStory.participants?.length ||
                activeStory.tags?.length) ? (
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", marginLeft: "10px" }}>
                  {activeStory.cameras?.map((c) => {
                    const camName = getCameraName(c);
                    return camName ? (
                      <Badge
                        key={c}
                        variant="outline"
                        className="h-4 px-1.5 text-[0.65rem] font-bold border-0 bg-[#ff9933] text-black rounded-full"
                      >
                        {camName}
                      </Badge>
                    ) : null;
                  })}
                  {activeStory.participants?.map((pId) => {
                    const pObj = participants.find((p) => p.id.toLowerCase() === pId.toLowerCase());
                    return pObj ? (
                      <ParticipantBadge key={pId} participant={pObj} variant="pill" size="sm" />
                    ) : (
                      <Badge
                        key={pId}
                        variant="destructive"
                        className="h-4 px-1.5 text-[0.65rem] font-bold"
                      >
                        {pObj?.name || "Unknown"}
                      </Badge>
                    );
                  })}
                  {activeStory.tags?.map((t) => {
                    const c = getTagColor(t);
                    return (
                      <Badge
                        key={t}
                        variant="outline"
                        className="h-4 px-1.5 text-[0.65rem] font-bold border-0 uppercase"
                        style={{ background: c, color: isColorLight(c) ? "#000" : "#fff" }}
                      >
                        {getTagLabel(t)}
                      </Badge>
                    );
                  })}
                </div>
              ) : null}
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                flexWrap: "nowrap",
                flexShrink: 0,
              }}
            >
              {isCreatingStory ? (
                <StoryCreationInput
                  onCreate={handleCreateNewStory}
                  onCancel={handleCancelCreation}
                />
              ) : showConfirmComplete ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-green-500">
                    {t("logger.console.completeStory")}
                  </span>
                  <Button
                    size="sm"
                    className="bg-green-500 text-black hover:bg-green-500/80"
                    onClick={() => {
                      onCompleteStory(activeStory.id);
                      setShowConfirmComplete(false);
                    }}
                  >
                    {t("logger.console.yes")}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowConfirmComplete(false)}
                  >
                    {t("logger.console.no")}
                  </Button>
                </div>
              ) : showConfirmClose ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-[var(--color-data-story)]">
                    {t("logger.console.newStoryClosesCurrent")}
                  </span>
                  <Button
                    size="sm"
                    className="bg-[var(--color-data-story)] text-black hover:bg-[var(--color-data-story)]/80"
                    onClick={() => {
                      setShowConfirmClose(false);
                      setIsCreatingStory(true);
                    }}
                  >
                    {t("logger.console.ok")}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setShowConfirmClose(false)}>
                    {t("logger.console.cancel")}
                  </Button>
                </div>
              ) : (
                <>
                  <Button
                    data-testid="console-btn-complete-story"
                    variant="outline"
                    size="sm"
                    className="border-green-500 text-green-500 bg-green-500/20 hover:bg-green-500/30"
                    onClick={() => setShowConfirmComplete(true)}
                    disabled={!activeRundown}
                  >
                    {t("logger.console.complete")}
                  </Button>
                  <Button
                    data-testid="console-btn-new-story"
                    size="sm"
                    className="bg-[var(--color-data-story)] text-black hover:bg-[var(--color-data-story)]/80"
                    onClick={() => {
                      const isReallyActive =
                        activeStory &&
                        activeStory.id !== "EMPTY" &&
                        activeStory.status === "active";
                      if (isReallyActive) setShowConfirmClose(true);
                      else setIsCreatingStory(true);
                    }}
                    disabled={!activeRundown}
                  >
                    {t("logger.console.newStory")}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        <Separator className="bg-[var(--color-panel-border)]" />

        {/* ROW 2: global log defaults. Kept separate from story actions so this
            compact control strip has the same hierarchy as the pre-RESUMEN UI. */}
        <div
          data-testid="console-header-row2"
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "16px",
            flexWrap: "nowrap",
            overflowX: "auto",
          }}
        >
          {/* CAMERAS SECTION (Dropdown) */}
          <div data-testid="console-cameras-section" className="flex shrink-0 items-center gap-3">
            <div className="flex items-center gap-2">
              <i
                className="bx bx-video text-lg text-muted-foreground"
                title={t("logger.console.camerasTitle", "Cámaras")}
              />
              <DropdownMenu>
                <DropdownMenuTrigger className="inline-flex shrink-0 items-center justify-center rounded-md border border-[#ffaa00]/50 bg-transparent px-2 text-xs font-medium text-[#ffaa00] shadow-xs hover:bg-[#ffaa00]/10 hover:text-[#ffaa00] h-6 cursor-pointer">
                  <i className="bx bx-plus mr-1" />
                  {selectedCameras.length === 0
                    ? t("logger.console.select", "Seleccionar")
                    : cameras.find((c) => c.id === selectedCameras[0])?.name ||
                      t("logger.console.selected", "Seleccionada")}
                  <i className="bx bx-chevron-down ml-1" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuRadioGroup
                    value={selectedCameras.length > 0 ? selectedCameras[0] : ""}
                    onValueChange={(val) => {
                      setSelectedCameras([val]);
                      // Immediately persist the stream change to the active story
                      if (onUpdateStory && activeStory && activeStory.id !== "EMPTY") {
                        onUpdateStory({ ...activeStory, cameras: [val] });
                      }
                    }}
                  >
                    {cameras
                      .filter((cam) => !hiddenCameraIds.includes(cam.id))
                      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
                      .map((cam) => (
                        <DropdownMenuRadioItem key={cam.id} value={cam.id}>
                          {cam.name}
                        </DropdownMenuRadioItem>
                      ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="ghost"
                size="icon-xs"
                className={
                  hiddenCameraIds.length > 0
                    ? "text-[var(--color-accent-warning)]"
                    : "text-muted-foreground"
                }
                onClick={() => setIsCameraFilterOpen(true)}
                title={t("logger.console.filterCameras")}
              >
                <i className="bx bx-slider-alt" />
              </Button>
            </div>

            <Separator orientation="vertical" className="h-4 mx-1" />

            <div className="flex items-center gap-2">
              <i
                className="bx bx-movie-play text-lg text-muted-foreground"
                title={t("logger.console.directorTitle", "Director")}
              />
              <DropdownMenu>
                <DropdownMenuTrigger className="inline-flex shrink-0 items-center justify-center rounded-md border border-input bg-transparent px-2 text-xs font-medium text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground h-6 cursor-pointer">
                  {!selectedDirector
                    ? t("logger.console.select", "Seleccionar")
                    : directors.find((d) => d.id === selectedDirector)?.name || selectedDirector}
                  <i className="bx bx-chevron-down ml-1" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuRadioGroup
                    value={selectedDirector || ""}
                    onValueChange={(val) => {
                      setSelectedDirector(val);
                    }}
                  >
                    {directors
                      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
                      .map((d) => (
                        <DropdownMenuRadioItem key={d.id} value={d.id}>
                          {d.name}
                        </DropdownMenuRadioItem>
                      ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <Separator orientation="vertical" className="h-4 mx-1" />

            {/* Defaults applied to every new entry, independent of the
                  temporary selection matrix below. */}
            <div className="flex shrink-0 items-center gap-3">
              <div data-testid="console-location-section" className="flex items-center gap-2">
                <i
                  className="bx bx-map text-lg text-muted-foreground"
                  title={t("logger.console.locationTitle", "Ubicación")}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger
                    disabled={locationTags.length === 0}
                    className="inline-flex shrink-0 items-center justify-center rounded-md border border-input bg-transparent px-2 text-xs font-medium text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground h-6 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {!selectedLocation
                      ? t("logger.console.select", "Seleccionar")
                      : locationTags.find((location) => location.id === selectedLocation)?.label ||
                        getTagLabel(selectedLocation)}
                    <i className="bx bx-chevron-down ml-1" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem
                      onClick={() => setSelectedLocation(undefined)}
                      className="text-muted-foreground italic"
                    >
                      {t("logger.console.none", "Ninguno")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuRadioGroup
                      value={selectedLocation || ""}
                      onValueChange={setSelectedLocation}
                    >
                      {locationTags.map((location) => (
                        <DropdownMenuRadioItem key={location.id} value={location.id}>
                          {location.label}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <Separator orientation="vertical" className="h-4 mx-1" />

              <div
                data-testid="console-global-participants-section"
                className="flex items-center gap-2"
              >
                <i
                  className="bx bx-group text-lg text-muted-foreground"
                  title={t("logger.console.participantsTitle", "Participantes")}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger className="inline-flex shrink-0 items-center justify-center rounded-md border border-input bg-transparent px-2 text-xs font-medium text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground h-6 cursor-pointer">
                    {globalSelectedParticipants.length === 0
                      ? t("logger.console.select", "Seleccionar")
                      : globalSelectedParticipants.length === 1
                        ? getParticipantName(globalSelectedParticipants[0])
                        : t("logger.console.nSelected", "{{n}} seleccionados", {
                            n: globalSelectedParticipants.length,
                          })}
                    <i className="bx bx-chevron-down ml-1" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-60 max-h-80 overflow-auto">
                    {globalSelectedParticipants.length > 0 && (
                      <>
                        <DropdownMenuItem
                          onClick={() => setGlobalSelectedParticipants([])}
                          className="text-muted-foreground italic"
                        >
                          {t("logger.console.clear", "Limpiar")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    {participants
                      .slice()
                      .sort((a, b) => {
                        if (a.number != null && b.number != null) return a.number - b.number;
                        return (a.alias || a.name || "").localeCompare(b.alias || b.name || "");
                      })
                      .map((participant) => {
                        const checked = globalSelectedParticipants.includes(participant.id);
                        return (
                          <DropdownMenuCheckboxItem
                            key={participant.id}
                            checked={checked}
                            onCheckedChange={(selected) => {
                              setGlobalSelectedParticipants((previous) =>
                                selected
                                  ? [...previous, participant.id]
                                  : previous.filter((id) => id !== participant.id),
                              );
                            }}
                            onSelect={(event) => event.preventDefault()}
                          >
                            {getParticipantName(participant.id)}
                          </DropdownMenuCheckboxItem>
                        );
                      })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <Separator orientation="vertical" className="h-4 mx-1" />

              <Badge
                data-testid="console-otf-mode"
                variant={isOtfMode ? "default" : "outline"}
                className={`cursor-pointer select-none ${
                  isOtfMode
                    ? "bg-[#00e5ff] text-black border-transparent hover:bg-[#00e5ff]/90"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setIsOtfMode((enabled) => !enabled)}
                title="Activar para registrar logs y transcripciones como OTF"
              >
                <i className={`bx ${isOtfMode ? "bx-microphone" : "bx-microphone-off"} mr-1`} />
                OTF MODE
              </Badge>
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          padding: "12px 16px 8px 16px",
          flex: 1,
          overflow: "hidden",
          gap: "12px",
        }}
      >
        <ResizablePanelGroup orientation="vertical" className="flex-1 min-h-0">
          {/* MAIN CONTENT AREA: Transcriptions & Logs (Side-by-Side) — hidden on mobile */}
          {!isMobile && (
            <>
              <ResizablePanel defaultSize={70} minSize={25}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    height: "100%",
                    overflow: "hidden",
                  }}
                >
                  {/* --- LIVE TRANSCRIPTIONS PANEL (PoC) --- (Left Column / Top on mobile) */}
                  <div
                    style={{
                      flex: showTranscriptions ? 1 : isMobile ? "0 0 40px" : "0 0 40px",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      marginRight: isMobile ? 0 : "16px",
                      marginBottom: isMobile ? "12px" : 0,
                      transition: "flex 0.3s ease",
                    }}
                  >
                    <LiveTranscriptionsPanel
                      baseTc={frozenTC || currentDisplayTC}
                      participants={participants}
                      isCollapsed={!showTranscriptions}
                      onToggleCollapse={() => setShowTranscriptions(!showTranscriptions)}
                      speakerAssignments={speakerAssignments}
                      isAiActive={isAiActive}
                      onStartAi={handleStartAi}
                      onStopAi={handleStopAi}
                      isOtfMode={isOtfMode}
                      onOtfModeChange={setIsOtfMode}
                      onTranscriptDragChange={setDraggedTranscriptionTimecodes}
                      onSpeakerMap={(source, target, timestamp) => {
                        setSpeakerAssignments((prev) => [
                          ...prev,
                          {
                            speakerId: source,
                            name: target,
                            assignedAt: timestamp !== undefined ? Number(timestamp) : Date.now(),
                          },
                        ]);
                      }}
                      onSelectText={(text, tc, speaker) => {
                        // Paste text cleanly into input and auto-add speaker tag
                        const cleanText = text
                          .replace(/^\[[^\]]+\]\s*/, "")
                          .replace(/^\[[^\]]+\]:\s*/, "");
                        const newText =
                          (inputValue ? inputValue + "\n" : "") + `[${speaker}] "${cleanText}"`;
                        setInputValue(newText);

                        // Optional: Automatically focus the input for fast typing
                        if (inputRef.current) {
                          inputRef.current.focus();
                          // Put cursor at end
                          setTimeout(() => {
                            if (inputRef.current)
                              inputRef.current.selectionStart = inputRef.current.value.length;
                          }, 10);
                        }
                      }}
                      onQuickLog={(
                        text,
                        tc,
                        speaker,
                        isOtf,
                        sourceTimestamp,
                        startOffset,
                        endOffset,
                        channel,
                      ): boolean => {
                        // Quick-logged transcriptions become story entries too,
                        // so they require a stream (per-entry STREAM chip or the
                        // story default). Bail before dedup so a retry works.
                        const quickCameras =
                          selectedLogCameras.length > 0 ? selectedLogCameras : selectedCameras;
                        if (!ensureStream(quickCameras)) return false;
                        const cleanText = text
                          .replace(/^\[[^\]]+\]\s*/, "")
                          .replace(/^\[[^\]]+\]:\s*/, "");
                        const logText = `[${speaker}] "${cleanText}"`;
                        const quickLogKey = `${tc}\u0001${logText}`;
                        const now = Date.now();
                        for (const [key, createdAt] of recentQuickLogsRef.current) {
                          if (now - createdAt > 2_000) recentQuickLogsRef.current.delete(key);
                        }
                        if (recentQuickLogsRef.current.has(quickLogKey)) return true;
                        recentQuickLogsRef.current.set(quickLogKey, now);
                        const { extractedParticipants, extractedCameras, extractedTags } =
                          extractMetadata(logText);

                        const finalParticipants = Array.from(
                          new Set([
                            ...globalSelectedParticipants,
                            ...selectedParticipants,
                            ...extractedParticipants,
                          ]),
                        );
                        const effectiveLogCameras =
                          selectedLogCameras.length > 0 ? selectedLogCameras : selectedCameras;
                        const finalCameras = Array.from(
                          new Set([...effectiveLogCameras, ...extractedCameras]),
                        );
                        const finalTags = Array.from(
                          new Set([
                            ...selectedTags,
                            ...extractedTags,
                            ...(selectedLocation ? [selectedLocation] : []),
                          ]),
                        );

                        // Compute the absolute end from the source timestamp plus
                        // the utterance duration. Provider offsets are session-
                        // relative, so adding endOffset directly would overcount.
                        let endAt: string | undefined;
                        if (
                          startOffset != null &&
                          endOffset != null &&
                          typeof sourceTimestamp === "number"
                        ) {
                          endAt = new Date(
                            sourceTimestamp + Math.max(0, endOffset - startOffset) * 1000,
                          ).toISOString();
                        }

                        const submittedAt = Date.now();
                        const newLog: LogEntry = {
                          id: createTempLogId(isOtf ? "otf" : "transcription"),
                          tc: tc,
                          text: logText,
                          cameras: finalCameras,
                          streams: channel ? [`STREAM ${channel}`] : undefined,
                          participants: finalParticipants,
                          rating: rating > 0 ? rating : undefined,
                          tags: finalTags.length > 0 ? finalTags : undefined,
                          status: "synced",
                          rundownId: viewingMilestoneId || undefined,
                          type: isOtf ? "otf" : "transcription",
                          originType: isOtf ? "transcript" : undefined,
                          createdAt: submittedAt,
                          startAt:
                            typeof sourceTimestamp === "number"
                              ? new Date(sourceTimestamp).toISOString()
                              : new Date(submittedAt).toISOString(),
                          endAt,
                          startOffset,
                          endOffset,
                        } as any;

                        if (onAddLog) {
                          const added = onAddLog(newLog);
                          if (added === false) {
                            recentQuickLogsRef.current.delete(quickLogKey);
                            return false;
                          }
                        }
                        return true;
                      }}
                    />
                  </div>

                  {/* Recent Logs List with Chips (Right Column) */}
                  <RecentLogsPanel
                    logs={lastLogs}
                    participants={participants}
                    directors={directors}
                    editingLogId={editingLogId}
                    onEditLog={handleEditLog}
                    onUpdateLog={(updatedLog) => {
                      if (onUpdateLog) onUpdateLog(updatedLog);
                    }}
                    onDeleteLog={(logId) => {
                      if (onDeleteLog) onDeleteLog(logId);
                      if (editingLogId === logId) cancelEdit();
                    }}
                    onReorderLogs={onReorderLogs}
                    draggedTranscriptionTimecodes={draggedTranscriptionTimecodes}
                    onDrop={(text, isOtf, sources, startAts, endAts, words, channels, insertAt) => {
                      // Dropped transcriptions become story entries too, so they
                      // require a stream. Block the whole drop up-front when none
                      // is selected (per-entry STREAM chip or story default).
                      const dropCameras =
                        selectedLogCameras.length > 0 ? selectedLogCameras : selectedCameras;
                      if (!ensureStream(dropCameras)) return;
                      const lines = text.split("\n").filter((l) => l.trim().length > 0);
                      const newLogs: LogEntry[] = [];

                      for (const [lineIdx, line] of lines.entries()) {
                        const tcMatch = line.match(/\[(\d{2}:\d{2}:\d{2}:\d{2})\]/);
                        let cleanLine = line.trim();
                        let tc = frozenTC || currentDisplayTC;

                        if (tcMatch) {
                          tc = tcMatch[1];
                          cleanLine = cleanLine.replace(tcMatch[0], "").trim();
                        }

                        const isDuplicate =
                          lastLogs.some((l) => l.tc === tc && l.text === cleanLine) ||
                          newLogs.some((l) => l.tc === tc && l.text === cleanLine);

                        if (isDuplicate) continue;

                        const { extractedParticipants, extractedCameras, extractedTags } =
                          extractMetadata(cleanLine);
                        const finalParticipants = Array.from(
                          new Set([
                            ...globalSelectedParticipants,
                            ...selectedParticipants,
                            ...extractedParticipants,
                          ]),
                        );
                        const effectiveLogCameras =
                          selectedLogCameras.length > 0 ? selectedLogCameras : selectedCameras;
                        const finalCameras = Array.from(
                          new Set([...effectiveLogCameras, ...extractedCameras]),
                        );
                        const finalTags = Array.from(
                          new Set([
                            ...selectedTags,
                            ...extractedTags,
                            ...(selectedLocation ? [selectedLocation] : []),
                          ]),
                        );

                        // sources[i] is the relay's source_timestamp for that line,
                        // not browser receipt/drop time.
                        const sourceMs =
                          Array.isArray(sources) && typeof sources[lineIdx] === "number"
                            ? sources[lineIdx]
                            : undefined;

                        const startAtMs =
                          Array.isArray(startAts) && typeof startAts[lineIdx] === "number"
                            ? startAts[lineIdx]
                            : undefined;

                        const endAtMs =
                          Array.isArray(endAts) && typeof endAts[lineIdx] === "number"
                            ? endAts[lineIdx]
                            : undefined;

                        const wordsArr =
                          Array.isArray(words) && Array.isArray(words[lineIdx])
                            ? words[lineIdx]
                            : undefined;

                        const channel =
                          Array.isArray(channels) && typeof channels[lineIdx] === "number"
                            ? channels[lineIdx]
                            : undefined;

                        let absoluteEndAt = undefined;
                        if (
                          typeof startAtMs === "number" &&
                          typeof endAtMs === "number" &&
                          sourceMs
                        ) {
                          const durationMs = (endAtMs - startAtMs) * 1000;
                          absoluteEndAt = new Date(sourceMs + durationMs).toISOString();
                        }

                        const submittedAt = Date.now();
                        const newLog: LogEntry = {
                          id: createTempLogId(isOtf ? "otf" : "transcription"),
                          tc: tc,
                          text: cleanLine,
                          cameras: finalCameras,
                          streams: channel ? [`STREAM ${channel}`] : undefined,
                          participants: finalParticipants,
                          rating: rating > 0 ? rating : undefined,
                          tags: finalTags.length > 0 ? finalTags : undefined,
                          status: "synced",
                          type: isOtf ? "otf" : "transcription",
                          originType: isOtf ? "transcript" : undefined,
                          rundownId: viewingMilestoneId || undefined,
                          createdAt: submittedAt,
                          startAt: sourceMs
                            ? new Date(sourceMs).toISOString()
                            : new Date(submittedAt).toISOString(),
                          endAt: absoluteEndAt,
                          startOffset: startAtMs,
                          endOffset: endAtMs,
                          words: wordsArr,
                        } as any;

                        const added = onAddLog ? onAddLog(newLog) : true;
                        // Stop iterating on the first failure — otherwise every
                        // dropped line fires another Sentry event for the same
                        // underlying cause (e.g. session expired).
                        if (added === false) break;
                        newLogs.push(newLog);
                      }

                      if (newLogs.length > 0 && typeof insertAt === "number" && onReorderLogs) {
                        const orderedIds = lastLogs.map((log) => log.id);
                        orderedIds.splice(
                          Math.max(0, Math.min(insertAt, orderedIds.length)),
                          0,
                          ...newLogs.map((log) => log.id),
                        );
                        onReorderLogs(orderedIds);
                      }
                    }}
                    getCameraName={getCameraName}
                    getParticipantName={getParticipantName}
                    getTagColor={getTagColor}
                    getTagLabel={getTagLabel}
                  />
                </div>
              </ResizablePanel>

              <ResizableHandle withHandle className="my-1 data-[orientation=horizontal]:h-2" />
            </>
          )}

          <ResizablePanel defaultSize={isMobile ? 100 : 30} minSize={10}>
            {/* BOTTOM STACK (Participants, Cameras, Tags) - Scrollable if too large */}
            <ResizablePanelGroup
              orientation="horizontal"
              defaultLayout={matrixSplitLayout}
              onLayoutChanged={(layout) => {
                setMatrixSplitLayout(layout);
                try {
                  window.localStorage.setItem(MATRIX_SPLIT_KEY, JSON.stringify(layout));
                } catch {
                  // ignore quota errors
                }
              }}
              data-testid="console-matrix-stack"
              className="h-full min-h-0"
            >
              {/* LEFT COLUMN: PARTICIPANTS */}
              <ResizablePanel id="matrix-participants" defaultSize="25%" minSize="10%">
                <div
                  data-testid="console-matrix-left-column"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minWidth: 0,
                    paddingRight: "12px",
                    overflowY: "auto",
                    scrollbarGutter: "stable",
                  }}
                >
                  {/* PARTICIPANTS SECTION (Auto Height, No Scroll) */}
                  <div
                    data-testid="console-participants-section"
                    style={{ display: "flex", flexDirection: "column", flexShrink: 0 }}
                  >
                    <div style={{ width: "100%" }}>
                      <ParticipantsPanel
                        participants={participants}
                        title={t("logger.console.participantsTitle")}
                        mode={participantMode}
                        onModeChange={setParticipantMode}
                        size={participantSize}
                        onSizeChange={setParticipantSize}
                        selectedIds={selectedParticipants}
                        hiddenIds={hiddenParticipantIds}
                        onSettingsClick={() => setIsFilterOpen(true)}
                        selectedColor="var(--color-accent-danger)"
                        modeLabels={{
                          photo: t("logger.console.photo"),
                          number: t("logger.console.text"),
                          code: t("logger.console.code"),
                        }}
                        modeTooltip={t("logger.console.cycleDisplay")}
                        settingsTooltip={t("logger.console.filterParticipants")}
                        onDropItem={(p, data, sourceTimestamp) => {
                          if (data) {
                            const pName = (p.alias || p.name).toUpperCase();
                            setSpeakerAssignments((prev) => [
                              ...prev,
                              {
                                speakerId: data,
                                name: pName,
                                assignedAt:
                                  sourceTimestamp !== undefined ? sourceTimestamp : Date.now(),
                              },
                            ]);
                          }
                        }}
                        onSelect={(p) => {
                          const name = p.alias || p.code || p.name;
                          const tagText = `[${name}]`;
                          const caret = inputRef.current?.selectionStart ?? inputValue.length;

                          const before = inputValue.slice(0, caret);
                          const after = inputValue.slice(caret);

                          // Detect a [NAME] tag to remove. Two cases:
                          //  1) Caret is INSIDE the brackets (legacy behavior).
                          //  2) Caret is right AFTER the closing bracket, optionally
                          //     separated by whitespace — e.g. "[FLOR] |" or "[FLOR]|".
                          let bracketStart = -1;
                          let bracketEnd = -1;
                          let bracketName = "";

                          const openIdx = before.lastIndexOf("[");
                          const closeBeforeIdx = before.lastIndexOf("]");
                          if (openIdx > closeBeforeIdx) {
                            // Case 1: caret is inside an unclosed [...] segment.
                            const closeRel = after.indexOf("]");
                            const openRel = after.indexOf("[");
                            if (closeRel !== -1 && (openRel === -1 || closeRel < openRel)) {
                              bracketStart = openIdx;
                              bracketEnd = caret + closeRel + 1;
                              bracketName = inputValue.slice(openIdx + 1, bracketEnd - 1);
                            }
                          } else {
                            // Case 2: caret is after a recently inserted [NAME] (with
                            // optional trailing whitespace). Walk back over spaces
                            // and check if we land on a `]`.
                            let i = caret;
                            while (i > 0 && /\s/.test(inputValue[i - 1])) i--;
                            if (i > 0 && inputValue[i - 1] === "]") {
                              const tagClose = i - 1;
                              const tagOpen = inputValue.lastIndexOf("[", tagClose);
                              if (tagOpen !== -1) {
                                bracketStart = tagOpen;
                                bracketEnd = tagClose + 1;
                                bracketName = inputValue.slice(tagOpen + 1, tagClose);
                              }
                            }
                          }

                          if (bracketStart !== -1 && bracketName === name) {
                            const trimEnd =
                              inputValue[bracketEnd] === " " ? bracketEnd + 1 : bracketEnd;
                            const newValue =
                              inputValue.slice(0, bracketStart) + inputValue.slice(trimEnd);
                            setInputValue(newValue);
                            const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                            const stillPresent = new RegExp(`\\[${escaped}\\]`).test(newValue);
                            if (!stillPresent) {
                              setSelectedParticipants((prev) => prev.filter((id) => id !== p.id));
                            }
                            setTimeout(() => {
                              if (inputRef.current) {
                                inputRef.current.focus();
                                inputRef.current.selectionStart = bracketStart;
                                inputRef.current.selectionEnd = bracketStart;
                              }
                            }, 0);
                            return;
                          }

                          setSelectedParticipants((prev) =>
                            prev.includes(p.id) ? prev : [...prev, p.id],
                          );
                          setInputValue((prev) => {
                            const needsSpace = prev.length > 0 && !prev.endsWith(" ");
                            return prev + (needsSpace ? " " : "") + tagText + " ";
                          });
                          if (!frozenTC) freeze();
                          setTimeout(() => {
                            if (inputRef.current) {
                              inputRef.current.focus();
                              const len = inputRef.current.value.length;
                              inputRef.current.selectionStart = len;
                              inputRef.current.selectionEnd = len;
                            }
                          }, 0);
                        }}
                        onInfo={(p) => setViewingParticipant(p)}
                      />
                    </div>
                  </div>
                </div>
              </ResizablePanel>
              <ResizableHandle
                withHandle
                className="mx-1 bg-white/10 hover:bg-white/20 transition-colors"
              />
              {/* RIGHT COLUMN: STREAM + TAGS */}
              <ResizablePanel id="matrix-stream-tags" defaultSize="75%" minSize="20%">
                <div
                  data-testid="console-matrix-right-column"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minWidth: 0,
                    paddingLeft: "12px",
                    overflowY: "auto",
                    scrollbarGutter: "stable",
                  }}
                >
                  {/* ── TOP CONTROLS BAR (shared by STREAM + TAGS) ── */}
                  <div
                    data-testid="console-matrix-controls-bar"
                    className="flex items-center justify-end gap-2"
                    style={{ marginBottom: "6px", flexShrink: 0 }}
                  >
                    <Badge
                      data-testid="console-btn-tag-icon-mode"
                      variant="outline"
                      className={`cursor-pointer select-none gap-1 ${!showTagText ? "text-[var(--color-accent-success)] border-[var(--color-accent-success)]/30" : "text-muted-foreground"}`}
                      onClick={() => setShowTagText((prev) => !prev)}
                      title={
                        !showTagText
                          ? t("logger.console.showText")
                          : t("logger.console.showIconsOnly")
                      }
                    >
                      <i className="bx bx-face"></i>
                      {!showTagText ? t("logger.console.textMode") : t("logger.console.iconMode")}
                    </Badge>
                    <SizeSlider
                      data-testid="console-slider-tags"
                      value={tagSize}
                      onChange={setTagSize}
                      min={showTagText ? 60 : 24}
                      max={showTagText ? 300 : 200}
                    />
                  </div>
                  {/* ── STREAM SECTION ── */}
                  <div
                    data-testid="console-stream-section"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px",
                      flexShrink: 0,
                      marginTop: "0",
                      width: "100%",
                    }}
                  >
                    {/* Stream title row */}
                    <div
                      style={{
                        fontSize: "0.65rem",
                        textTransform: "uppercase",
                        color: "var(--color-text-secondary)",
                        fontWeight: "bold",
                        marginBottom: "4px",
                      }}
                    >
                      STREAM
                    </div>

                    {/* Stream chips */}
                    <div className="flex flex-wrap gap-1 items-center">
                      {cameras
                        .filter((cam) => !hiddenCameraIds.includes(cam.id))
                        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
                        .map((cam) => {
                          const isSelected = selectedLogCameras.includes(cam.id);
                          const chipHeight = showTagText ? Math.max(20, tagSize / 4) : tagSize;
                          const chipFontSize = showTagText
                            ? `${Math.min(0.95, 0.55 + tagSize / 500)}rem`
                            : `${Math.max(12, tagSize * 0.5)}px`;
                          return (
                            <Badge
                              key={cam.id}
                              variant="outline"
                              className={`cursor-pointer select-none whitespace-nowrap gap-1 transition-all font-bold uppercase ${
                                isSelected
                                  ? "bg-[#ffaa00] text-black border-[#ffaa00] hover:bg-[#ffaa00]/90 shadow-[0_0_6px_rgba(255,170,0,0.4)]"
                                  : "text-[#ffaa00]/70 border-[#ffaa00]/50 hover:border-[#ffaa00] hover:text-[#ffaa00] bg-transparent"
                              }`}
                              style={{
                                height: `${chipHeight}px`,
                                minWidth: !showTagText ? `${tagSize}px` : undefined,
                                padding: showTagText ? "0 8px" : "0",
                                fontSize: chipFontSize,
                              }}
                              onClick={() => {
                                setSelectedLogCameras((prev) =>
                                  prev.includes(cam.id) && prev.length === 1 ? [] : [cam.id],
                                );
                              }}
                            >
                              {isSelected && (
                                <i className="bx bx-video" style={{ fontSize: chipFontSize }} />
                              )}
                              {cam.name}
                            </Badge>
                          );
                        })}
                    </div>
                  </div>

                  {/* ── TAGS SECTION ── */}
                  <div
                    data-testid="console-tags-section"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px",
                      flexShrink: 0,
                      marginTop: "6px",
                      width: "100%",
                    }}
                  >
                    {/* Tags Header */}
                    <div
                      style={{
                        fontSize: "0.65rem",
                        textTransform: "uppercase",
                        color: "var(--color-text-secondary)",
                        fontWeight: "bold",
                        marginBottom: "2px",
                      }}
                    >
                      TAGS
                    </div>

                    <div style={{ display: "flex", gap: "8px" }}>
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "12px",
                          alignItems: "flex-start",
                        }}
                      >
                        {categories.map((cat) => (
                          <div
                            key={cat.id}
                            data-testid={`console-tag-category-${(cat.name || cat.id).toLowerCase().replace(/\s+/g, "-")}`}
                            style={{
                              paddingLeft: "2px",
                              display: "flex",
                              flexDirection: "column",
                              flex: "1 1 auto",
                              minWidth: "120px",
                            }}
                          >
                            {showTagText && (
                              <div
                                style={{
                                  fontSize: "0.55rem",
                                  color: cat.color,
                                  fontWeight: "bold",
                                  marginBottom: "4px",
                                }}
                              >
                                {(cat.name || cat.id).toUpperCase()}
                              </div>
                            )}
                            <div className="flex flex-wrap gap-1 pr-0.5">
                              {tags
                                .filter((t) => t.categoryId === cat.id)
                                .map((t) => {
                                  const getLabel = () => {
                                    if (showTagText) return t.label;
                                    const emojiMatch = t.label.match(
                                      /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u,
                                    );
                                    return emojiMatch
                                      ? emojiMatch[0]
                                      : t.label.substring(0, 2).toUpperCase();
                                  };
                                  const displayLabel = getLabel();
                                  const isSelected = selectedTags.includes(t.id);

                                  return (
                                    <Badge
                                      key={t.id}
                                      variant="outline"
                                      onClick={() => toggleTag(t.id)}
                                      className="cursor-pointer select-none whitespace-nowrap gap-1 transition-all font-bold uppercase"
                                      style={{
                                        height: showTagText
                                          ? `${Math.max(20, tagSize / 4)}px`
                                          : `${tagSize}px`,
                                        minWidth: !showTagText ? `${tagSize}px` : undefined,
                                        padding: showTagText ? "0 8px" : "0",
                                        fontSize: showTagText
                                          ? `${Math.min(0.95, 0.55 + tagSize / 500)}rem`
                                          : `${Math.max(12, tagSize * 0.5)}px`,
                                        background: isSelected ? cat.color : "transparent",
                                        color: isSelected
                                          ? isColorLight(cat.color)
                                            ? "#000"
                                            : "#fff"
                                          : cat.color,
                                        borderColor: isSelected ? cat.color : `${cat.color}80`,
                                        boxShadow: isSelected ? `0 0 6px ${cat.color}66` : "none",
                                      }}
                                      title={t.label}
                                    >
                                      {displayLabel}
                                    </Badge>
                                  );
                                })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
            {/* Closes BottomStack */}
          </ResizablePanel>
        </ResizablePanelGroup>

        {/* ANCHORED BOTTOM PANEL (Input & Metadata) */}
        <div
          style={{
            padding: "12px 16px 24px 16px",
            borderTop: "1px solid var(--color-panel-border)",
            background: "rgba(0,0,0,0.2)",
            display: "flex",
            flexDirection: "row",
            gap: "16px",
            flexShrink: 0,
            alignItems: "stretch",
          }}
        >
          {/* LEFT COLUMN: TIME & CONTROLS */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              paddingRight: "16px",
              borderRight: "1px solid rgba(255,255,255,0.1)",
              width: "min(220px, 30vw)", // Responsive width
              flexShrink: 0,
              minWidth: 0,
              // The timecode uses cqw so its glyphs scale with this column,
              // rather than with the whole window.
              containerType: "inline-size",
            }}
          >
            {/* Timecode */}
            <div
              style={{
                height: "44px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                maxWidth: "100%",
              }}
            >
              <div
                className={frozenTC ? "blink" : ""}
                style={{
                  // A TC is always 11 glyphs (HH:MM:SS;FF).  At 12cqw it
                  // fits within the column's usable width at every breakpoint.
                  fontSize: "min(1.75rem, 12cqw)",
                  fontWeight: "900",
                  color: frozenTC ? "#ffcc00" : "#fff",
                  textAlign: "center",
                  fontFamily: "var(--font-mono)",
                  letterSpacing: "0.5px",
                  textShadow: "0 0 10px rgba(0,0,0,0.5)",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                data-testid="console-timecode"
              >
                {frozenTC || currentDisplayTC}
              </div>
            </div>

            {/* Controls */}
            <div
              data-testid="console-offset-controls"
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              {!frozenTC ? (
                <>
                  {editingLogId && (
                    <button
                      onClick={cancelEdit}
                      style={{
                        background: "#333",
                        color: "#aaa",
                        border: "none",
                        borderRadius: "4px",
                        padding: "0 8px",
                        height: "32px",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                        marginLeft: "5px",
                      }}
                    >
                      CANCEL
                    </button>
                  )}
                </>
              ) : (
                <Button
                  data-testid="console-btn-unfreeze"
                  variant="destructive"
                  size="sm"
                  onClick={unfreeze}
                >
                  UNFREEZE
                </Button>
              )}
            </div>

            {/* STARS */}
            <StarRating
              value={rating}
              onChange={(v) => {
                setRating(v);
                if (!frozenTC) freeze();
              }}
              size="2.2rem"
              gap="8px"
              data-testid="console-rating-stars"
              starTestIdPrefix="console-star"
            />
          </div>

          {/* RIGHT COLUMN: PREVIEW & INPUT */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            {/* Metadata Preview Bar (Now inside anchored panel) */}
            <div
              data-testid="console-metadata-preview"
              style={{
                display: "flex",
                gap: "8px",
                padding: "5px",
                background: "#111",
                borderRadius: "4px",
                overflowX: "auto",
                minHeight: "30px",
                alignItems: "center",
              }}
            >
              {selectedLogCameras.length === 0 &&
                selectedParticipants.length === 0 &&
                selectedTags.length === 0 &&
                rating === 0 && (
                  <span style={{ color: "#555", fontSize: "0.75rem", fontStyle: "italic" }}>
                    No metadata selected...
                  </span>
                )}
              {selectedLogCameras.map((c) => {
                const camName = getCameraName(c);
                return camName ? (
                  <Badge
                    key={c}
                    variant="outline"
                    render={
                      <button
                        type="button"
                        aria-label={`${t("logger.data.deselect")} ${camName}`}
                      />
                    }
                    onClick={() =>
                      setSelectedLogCameras((prev) => prev.filter((cameraId) => cameraId !== c))
                    }
                    className="h-4 px-1.5 text-[0.65rem] font-bold border-0 bg-[#ffaa00] text-black rounded-full"
                  >
                    {camName.toUpperCase()}
                    <XIcon data-icon="inline-end" aria-hidden="true" />
                  </Badge>
                ) : null;
              })}
              {selectedParticipants.map((pId) => {
                const pObj = participants.find((p) => p.id.toLowerCase() === pId.toLowerCase());
                if (!pObj) {
                  return (
                    <Badge
                      key={pId}
                      variant="destructive"
                      render={
                        <button type="button" aria-label={`${t("logger.data.deselect")} ${pId}`} />
                      }
                      onClick={() =>
                        setSelectedParticipants((prev) =>
                          prev.filter((participantId) => participantId !== pId),
                        )
                      }
                      className="h-4 px-1.5 text-[0.65rem] font-bold"
                    >
                      {pId}
                      <XIcon data-icon="inline-end" aria-hidden="true" />
                    </Badge>
                  );
                }
                const participantLabel = pObj.alias || pObj.name;
                return (
                  <ParticipantBadge
                    key={pId}
                    participant={pObj}
                    variant="pill"
                    size="sm"
                    onRemove={() =>
                      setSelectedParticipants((prev) =>
                        prev.filter((participantId) => participantId !== pId),
                      )
                    }
                    removeLabel={`${t("logger.data.deselect")} ${participantLabel}`}
                  />
                );
              })}
              {selectedTags.map((tagId) => {
                const color = getTagColor(tagId);
                const tagLabel = getTagLabel(tagId);
                return (
                  <Badge
                    key={tagId}
                    variant="outline"
                    render={
                      <button
                        type="button"
                        aria-label={`${t("logger.data.deselect")} ${tagLabel}`}
                      />
                    }
                    onClick={() =>
                      setSelectedTags((prev) =>
                        prev.filter((selectedTagId) => selectedTagId !== tagId),
                      )
                    }
                    className="h-4 px-1.5 text-[0.65rem] font-bold border-0 uppercase"
                    style={{
                      background: color,
                      color: isColorLight(color) ? "#000" : "#fff",
                    }}
                  >
                    {tagLabel}
                    <XIcon data-icon="inline-end" aria-hidden="true" />
                  </Badge>
                );
              })}
              <StarRating value={rating} readOnly size="1rem" />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: "10px",
                position: "relative",
              }}
            >
              <div style={{ position: "relative", flex: 1, display: "flex" }}>
                <Textarea
                  data-testid="console-log-input"
                  ref={inputRef}
                  autoFocus
                  disabled={!activeRundown}
                  value={inputValue}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setInputValue(newValue);
                    if (!frozenTC) freeze();

                    const bracketNames = (newValue.match(/\[(.*?)\]/g) || []).map((m) =>
                      m.slice(1, -1).trim().toUpperCase(),
                    );
                    const hasBracketFor = (p: any) =>
                      bracketNames.some(
                        (name) =>
                          (p.name && p.name.toUpperCase() === name) ||
                          (p.alias && p.alias.toUpperCase() === name) ||
                          (p.code && p.code.toUpperCase() === name) ||
                          (p.number != null && `${p.number}` === name),
                      );
                    setSelectedParticipants((prev) => {
                      const next = prev.filter((pId) => {
                        const p = participants.find((x) => x.id === pId);
                        return p ? hasBracketFor(p) : true;
                      });
                      return next.length === prev.length ? prev : next;
                    });
                    captureCaret();
                  }}
                  onSelect={captureCaret}
                  onKeyUp={captureCaret}
                  onMouseUp={captureCaret}
                  onBlur={captureCaret}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "copy";
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const text = e.dataTransfer.getData("text/plain");
                    if (text) {
                      const isOtf = e.altKey || e.metaKey;
                      const prefix = isOtf ? "[OTF] " : "";
                      const ta = inputRef.current;
                      // Prefer the cached caret (set on focus/select/keyup/
                      // mouseup/blur) — drops coming from another panel land
                      // here without focusing the textarea first, so
                      // `selectionStart` is unreliable.
                      const cached = lastCaretRef.current;
                      const dropStart =
                        cached?.start ?? ta?.selectionStart ?? ta?.value.length ?? 0;
                      const dropEnd = cached?.end ?? ta?.selectionEnd ?? dropStart;

                      setInputValue((prev) => {
                        const start = Math.min(dropStart, prev.length);
                        const end = Math.min(dropEnd, prev.length);
                        const before = prev.slice(0, start);
                        const after = prev.slice(end);
                        const needsLeadingNl =
                          before.length > 0 && !before.endsWith(" ") && !before.endsWith("\n");
                        const needsTrailingNl = after.length > 0 && !after.startsWith("\n");
                        const insertion =
                          (needsLeadingNl ? "\n" : "") +
                          prefix +
                          text +
                          (needsTrailingNl ? "\n" : after.length === 0 ? "\n" : "");
                        const next = before + insertion + after;
                        const caret = before.length + insertion.length;
                        lastCaretRef.current = { start: caret, end: caret };
                        setTimeout(() => {
                          if (inputRef.current) {
                            inputRef.current.focus();
                            inputRef.current.selectionStart = caret;
                            inputRef.current.selectionEnd = caret;
                          }
                        }, 0);
                        return next;
                      });
                      if (!frozenTC) freeze();
                    }
                  }}
                  rows={3}
                  className={`flex-1 min-h-0 resize-none bg-[#1a1a1a] text-lg border-2 ${
                    editingStoryData
                      ? "border-[var(--color-data-story)]"
                      : editingLogId
                        ? (editingOriginalLog as any)?.type === "transcription"
                          ? "border-purple-600"
                          : "border-red-500"
                        : frozenTC
                          ? "border-yellow-400"
                          : isHandoverPending
                            ? "border-yellow-400"
                            : "border-[#333]"
                  } focus-visible:ring-0`}
                  placeholder={editingStoryData ? "Story Title..." : "Type here..."}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submitLog();
                    } else if (
                      e.key === "Backspace" &&
                      inputValue === "" &&
                      selectedTags.length > 0
                    ) {
                      e.preventDefault();
                      setSelectedTags((prev) => prev.slice(0, -1));
                    }
                  }}
                />
                <button
                  onClick={() => {
                    setInputValue("");
                    setSelectedParticipants([]);
                    // Reset log stream to empty (story stream covers by default)
                    setSelectedLogCameras([]);
                    setSelectedTags([]);
                    setRating(0);
                    if (frozenTC) unfreeze();
                    inputRef.current?.focus();
                  }}
                  style={{
                    position: "absolute",
                    bottom: "8px",
                    right: "8px",
                    background: "rgba(0,0,0,0.5)",
                    color: "rgba(255, 255, 255, 0.8)",
                    border: "none",
                    borderRadius: "4px",
                    width: "24px",
                    height: "24px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.9rem",
                    opacity:
                      inputValue ||
                      selectedParticipants.length > 0 ||
                      selectedLogCameras.length > 0 ||
                      selectedTags.length > 0 ||
                      rating > 0
                        ? 1
                        : 0.3,
                    pointerEvents:
                      inputValue ||
                      selectedParticipants.length > 0 ||
                      selectedLogCameras.length > 0 ||
                      selectedTags.length > 0 ||
                      rating > 0
                        ? "auto"
                        : "none",
                  }}
                  title="Clear All"
                >
                  🗑️
                </button>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "row" : "column",
                  gap: "5px",
                  minWidth: isMobile ? 0 : "100px",
                  width: isMobile ? "100%" : undefined,
                }}
              >
                <Button
                  data-testid="console-btn-submit-log"
                  size="sm"
                  disabled={!activeRundown}
                  className={`${isMobile ? "flex-1 min-w-0" : "w-full"} ${editingStoryData ? "bg-[var(--color-data-story)] text-white" : editingLogId ? ((editingOriginalLog as any)?.type === "transcription" ? "bg-purple-600 text-white" : "bg-red-500 text-white") : "bg-green-500 text-black"} hover:opacity-90 disabled:opacity-50`}
                  onClick={submitLog}
                >
                  {editingStoryData || editingLogId ? "UPDATE" : "LOG"}
                </Button>
                {!editingLogId && !editingStoryData ? (
                  <Button
                    data-testid="console-btn-side-log"
                    size="sm"
                    disabled={!activeRundown}
                    className={`${isMobile ? "flex-1 min-w-0" : "w-full"} bg-[var(--color-accent-danger)] text-white/80 hover:bg-[var(--color-accent-danger)]/90 disabled:opacity-50`}
                    onClick={handleSideLog}
                  >
                    SIDE LOG
                  </Button>
                ) : (
                  <>
                    {editingLogId && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className={isMobile ? "flex-1 min-w-0" : "w-full"}
                        onClick={() => {
                          // Call either global delete or local delete
                          if (onExternalDelete && externalEditingLog) {
                            onExternalDelete();
                          } else {
                            if (onDeleteLog) onDeleteLog(editingLogId);
                          }
                          cancelEdit();
                        }}
                      >
                        DELETE
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      className={isMobile ? "flex-1 min-w-0" : "w-full"}
                      onClick={cancelEdit}
                    >
                      CANCEL
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
                .blink { animation: tc-blink 1s step-end infinite; }
                @keyframes tc-blink { 50% { opacity: 0.5; } }
                @keyframes pulse-warning { 0% { background: #ffcc00; } 50% { background: #ff9900; } 100% { background: #ffcc00; } }

                /* Custom Range Slider Styling */
                .custom-slider {
                    -webkit-appearance: none;
                    width: 100%;
                    background: transparent;
                }
                .custom-slider:focus {
                    outline: none;
                }
                
                /* Thumb (Handle) */
                .custom-slider::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    height: 10px;
                    width: 10px;
                    border-radius: 50%;
                    background: #555;
                    border: 1px solid #222;
                    cursor: pointer;
                    margin-top: -3px; /* Centers thumb on track */
                    box-shadow: 0 1px 3px rgba(0,0,0,0.5);
                    transition: background 0.2s, transform 0.2s;
                }
                .custom-slider::-webkit-slider-thumb:hover {
                    background: #777;
                    transform: scale(1.1);
                }

                /* Track (Bar) */
                .custom-slider::-webkit-slider-runnable-track {
                    width: 100%;
                    height: 4px;
                    cursor: pointer;
                    background: #2a2a2a;
                    border-radius: 2px;
                    border: 1px solid #111;
                }

                /* Story Input Placeholder Custom Color */
                .story-input::placeholder {
                    color: var(--color-data-story);
                    opacity: 0.5;
                }
            `}</style>

      {/* PARTICIPANT DETAILS MODAL */}
      {viewingParticipant && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.8)",
            zIndex: 9999,
            backdropFilter: "blur(5px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setViewingParticipant(null)}
        >
          <div
            style={{
              width: "500px",
              background: "rgba(20, 20, 25, 0.98)",
              border: "1px solid var(--color-panel-border)",
              borderRadius: "16px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
              padding: "30px",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setViewingParticipant(null)}
              style={{
                position: "absolute",
                top: "15px",
                right: "15px",
                background: "transparent",
                border: "none",
                color: "#666",
                fontSize: "1.5rem",
                cursor: "pointer",
              }}
            >
              ✕
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: "25px" }}>
              <div
                style={{
                  width: "140px",
                  height: "140px",
                  aspectRatio: "1 / 1",
                  flexShrink: 0,
                  borderRadius: "50%",
                  border: "4px solid var(--color-accent-danger)",
                  background:
                    viewingParticipant.image || viewingParticipant.avatarUrl
                      ? `url(${viewingParticipant.image || viewingParticipant.avatarUrl}) center/cover no-repeat`
                      : "#333",
                  boxShadow: "0 0 30px rgba(247, 45, 96, 0.4)",
                }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    color: "var(--color-accent-danger)",
                    fontWeight: "900",
                    fontSize: "2rem",
                    lineHeight: 1.1,
                    marginBottom: "5px",
                  }}
                >
                  {viewingParticipant.alias || viewingParticipant.name}
                </div>
                <div
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "1.2rem",
                    opacity: 0.9,
                    fontWeight: "bold",
                  }}
                >
                  {viewingParticipant.name}
                </div>
                <div style={{ display: "flex", gap: "10px", marginTop: "12px", flexWrap: "wrap" }}>
                  <span
                    style={{
                      background: "#333",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "0.9rem",
                      border: "1px solid #444",
                      color: "rgba(255, 255, 255, 0.8)",
                    }}
                  >
                    #{viewingParticipant.number || "--"}
                  </span>
                  {viewingParticipant.role && (
                    <span
                      style={{
                        background: "var(--color-accent-danger)",
                        color: "rgba(255, 255, 255, 0.8)",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "0.9rem",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    >
                      {viewingParticipant.role}
                    </span>
                  )}
                  {viewingParticipant.code && (
                    <span
                      style={{
                        fontFamily: "monospace",
                        background: "rgba(247, 45, 96, 0.2)",
                        color: "var(--color-accent-danger)",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "0.9rem",
                        fontWeight: "bold",
                      }}
                    >
                      {viewingParticipant.code}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {viewingParticipant.biography && (
              <div
                style={{
                  background: "rgba(255,255,255,0.05)",
                  padding: "20px",
                  borderRadius: "12px",
                  fontSize: "1.1rem",
                  color: "#ddd",
                  lineHeight: "1.5",
                  fontStyle: "italic",
                  borderLeft: "4px solid var(--color-accent-danger)",
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                "{viewingParticipant.biography}"
              </div>
            )}
          </div>
        </div>
      )}

      {/* Final Dialog Component */}
      <CustomDialog
        isOpen={dialog.isOpen}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        defaultValue={dialog.defaultValue}
        onConfirm={dialog.onConfirm}
        onCancel={() => setDialog((d) => ({ ...d, isOpen: false }))}
      />

      {/* PARTICIPANT FILTER MODAL */}
      <Dialog
        open={isFilterOpen}
        onOpenChange={(open) => {
          if (!open) setIsFilterOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-[400px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-[#ffaa00] text-lg font-black tracking-wider uppercase">
              CONFIGURE VISIBILITY
            </DialogTitle>
            <DialogDescription className="text-xs text-[#888]">
              Select the participants you want to see in the logging panel. Unchecked participants
              will be temporarily hidden.
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2.5">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs font-bold"
              onClick={() => setHiddenParticipantIds([])}
            >
              SHOW ALL
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs font-bold"
              onClick={() => setHiddenParticipantIds(participants.map((p) => p.id))}
            >
              HIDE ALL
            </Button>
          </div>

          <div className="overflow-y-auto flex-1 flex flex-col gap-2 pr-1 custom-scrollbar">
            {participants
              .sort((a, b) => (a.number || 0) - (b.number || 0))
              .map((p) => {
                const isHidden = hiddenParticipantIds.includes(p.id);
                return (
                  <div
                    key={p.id}
                    onClick={() =>
                      setHiddenParticipantIds((prev) =>
                        isHidden ? prev.filter((id) => id !== p.id) : [...prev, p.id],
                      )
                    }
                    className={`flex items-center gap-4 px-4 py-2.5 rounded-lg cursor-pointer transition-all duration-100 border ${isHidden ? "bg-white/[0.02] border-[#222]" : "bg-[#ffaa00]/5 border-[#ffaa00]/20"}`}
                  >
                    <div
                      className={`w-[22px] h-[22px] rounded-md border flex items-center justify-center ${isHidden ? "border-[#444] bg-transparent" : "border-[#ffaa00] bg-[#ffaa00]"}`}
                    >
                      {!isHidden && <i className="bx bx-check text-black text-lg font-bold"></i>}
                    </div>
                    <div className="flex items-center gap-2.5">
                      {p.image && (
                        <img
                          src={p.image}
                          draggable={false}
                          className="w-6 h-6 rounded-full border border-[#333]"
                        />
                      )}
                      <span
                        className={`text-sm tracking-wide ${isHidden ? "text-[#555] font-medium" : "text-[#eee] font-black"}`}
                      >
                        {p.number ? `${p.number}. ` : ""}
                        {(p.alias || p.name).toUpperCase()}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>

          <Button
            onClick={() => setIsFilterOpen(false)}
            className="mt-2 bg-[#ffaa00] text-black font-black hover:bg-[#ffaa00]/90 shadow-[0_4px_15px_rgba(255,170,0,0.2)]"
          >
            APPLY CONFIGURATION
          </Button>
        </DialogContent>
      </Dialog>

      {/* CAMERA FILTER MODAL */}
      <Dialog
        open={isCameraFilterOpen}
        onOpenChange={(open) => {
          if (!open) setIsCameraFilterOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-[400px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-[#ffaa00] text-lg font-black tracking-wider uppercase">
              CONFIGURE CAMERA VISIBILITY
            </DialogTitle>
            <DialogDescription className="text-xs text-[#888]">
              Select the cameras you want to see in the logging panel. Unchecked cameras will be
              temporarily hidden.
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2.5">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs font-bold"
              onClick={() => setHiddenCameraIds([])}
            >
              SHOW ALL
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs font-bold"
              onClick={() => setHiddenCameraIds(cameras.map((c) => c.id))}
            >
              HIDE ALL
            </Button>
          </div>

          <div className="overflow-y-auto flex-1 flex flex-col gap-2 pr-1 custom-scrollbar">
            {cameras
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((c) => {
                const isHidden = hiddenCameraIds.includes(c.id);
                return (
                  <div
                    key={c.id}
                    onClick={() =>
                      setHiddenCameraIds((prev) =>
                        isHidden ? prev.filter((id) => id !== c.id) : [...prev, c.id],
                      )
                    }
                    className={`flex items-center gap-4 px-4 py-2.5 rounded-lg cursor-pointer transition-all duration-100 border ${isHidden ? "bg-white/[0.02] border-[#222]" : "bg-[#ffaa00]/5 border-[#ffaa00]/20"}`}
                  >
                    <div
                      className={`w-[22px] h-[22px] rounded-md border flex items-center justify-center ${isHidden ? "border-[#444] bg-transparent" : "border-[#ffaa00] bg-[#ffaa00]"}`}
                    >
                      {!isHidden && <i className="bx bx-check text-black text-lg font-bold"></i>}
                    </div>
                    <div className="flex items-center gap-2.5">
                      <span
                        className={`text-sm tracking-wide ${isHidden ? "text-[#555] font-medium" : "text-[#eee] font-black"}`}
                      >
                        {c.name.toUpperCase()}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>

          <Button
            onClick={() => setIsCameraFilterOpen(false)}
            className="mt-2 bg-[#ffaa00] text-black font-black hover:bg-[#ffaa00]/90 shadow-[0_4px_15px_rgba(255,170,0,0.2)]"
          >
            APPLY CONFIGURATION
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
